import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Loader2, Sparkles, MapPin, ChevronRight, Calendar, AlertTriangle, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import type { DispatchTech } from "./dispatch-types";

type HelperValueTier = "low" | "medium" | "high";

interface AutoScheduleStop {
    jobId: string;
    jobTitle: string;
    siteName: string | null;
    arriveAt: string;
    departAt: string;
    coords: { lat: number; lng: number } | null;
    durationMinutes: number;
    distanceFromPrevMiles: number;
    leadTechId: string;
    helperUserIds: string[];
    helperValue: HelperValueTier;
    helperPrioritized: boolean;
    reason: string;
}

const HELPER_VALUE_BADGE: Record<HelperValueTier, { label: string; cls: string; tip: string }> = {
    high: {
        label: "H",
        cls: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700",
        tip: "High helper value — repair / replace / install or large-dollar estimate. Prioritized onto paired-lead days.",
    },
    medium: {
        label: "M",
        cls: "bg-sky-100 text-sky-700 border-sky-300 dark:bg-sky-900/40 dark:text-sky-200 dark:border-sky-700",
        tip: "Medium helper value — small repair / service call.",
    },
    low: {
        label: "L",
        cls: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700",
        tip: "Low helper value — routine testing. Falls through to solo-lead days when paired days are full of better-margin work.",
    },
};

interface AutoScheduleDayProposal {
    date: string;
    techId: string;
    techName: string;
    clusterKey: string;
    clusterLabel: string;
    stops: AutoScheduleStop[];
    totalBookedMinutes: number;
    capacityMinutes: number;
    fillRatio: number;
    productivePoints?: number;
    pointsTarget?: number;
    pointsCap?: number;
    weeklyPoints?: number;
    weeklyTarget?: number;
}

interface AutoScheduleSkipped {
    jobId: string;
    jobTitle: string;
    reason: string;
}

interface AutoScheduleResponse {
    fromDate: string;
    windowDays: number;
    noticeDays: number;
    shop: { lat: number; lng: number };
    clusterFillThreshold: number;
    proposals: AutoScheduleDayProposal[];
    skipped: AutoScheduleSkipped[];
    totals: {
        jobsConsidered: number;
        jobsProposed: number;
        jobsSkipped: number;
        techsConsidered: number;
        daysWithProposals: number;
    };
    apply: boolean;
    applied: number;
}

interface AutoScheduleDialogProps {
    open: boolean;
    onClose: () => void;
    response: AutoScheduleResponse | undefined;
    isLoading: boolean;
    /** Day key currently selected on the dispatch board. Used as the default
     *  starting point for the planning window. */
    fromDate: string;
}

function fmtTime(iso: string): string {
    return new Date(iso).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
    });
}

function fmtDate(dateKey: string): string {
    const d = new Date(`${dateKey}T12:00:00`);
    return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
}

function fillTone(ratio: number): string {
    if (ratio >= 0.85) return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200";
    if (ratio >= 0.5) return "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200";
    return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200";
}

export function AutoScheduleDialog({
    open,
    onClose,
    response,
    isLoading,
    fromDate,
}: AutoScheduleDialogProps) {
    const qc = useQueryClient();
    const [appliedDays, setAppliedDays] = useState<Set<string>>(new Set());
    const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

    /** Helper-name lookup so each stop renders e.g. "+ Chris Loma" instead
     *  of an opaque "+1 helper". Reuses the same cached technicians query
     *  the dispatch shell uses; this dialog will hit the cache. */
    const techsQuery = useQuery({
        queryKey: ["dispatch", "technicians"],
        queryFn: () => api.get<{ users: DispatchTech[] }>("/api/dispatch/technicians"),
        staleTime: 5 * 60_000,
    });
    const techNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const t of techsQuery.data?.users ?? []) {
            const name = `${t.first_name ?? ""} ${t.last_name ?? ""}`.trim() || t.email;
            map.set(t.id, name);
        }
        return map;
    }, [techsQuery.data]);
    const helperLabel = (ids: string[]): string => {
        if (ids.length === 0) return "";
        const names = ids.map((id) => techNameById.get(id) ?? "Helper");
        return ` · with ${names.join(", ")}`;
    };

    const applyDayMutation = useMutation({
        mutationFn: async (proposal: AutoScheduleDayProposal) => {
            // Re-runs the auto-scheduler in apply mode but scoped to just
            // this day's job IDs so other proposed days aren't committed
            // until the dispatcher reviews them.
            const jobIds = proposal.stops.map((s) => s.jobId);
            await api.post<AutoScheduleResponse>("/api/dispatch/auto-schedule", {
                fromDate,
                jobIds,
                apply: true,
            });
            return `${proposal.date}|${proposal.techId}`;
        },
        onSuccess: (key) => {
            setAppliedDays((prev) => new Set(prev).add(key));
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["dispatch-tech-routes"] });
        },
    });

    const applyAllMutation = useMutation({
        mutationFn: async (proposals: AutoScheduleDayProposal[]) => {
            const allJobIds = proposals.flatMap((p) => p.stops.map((s) => s.jobId));
            if (allJobIds.length === 0) return [];
            await api.post<AutoScheduleResponse>("/api/dispatch/auto-schedule", {
                fromDate,
                jobIds: allJobIds,
                apply: true,
            });
            return proposals.map((p) => `${p.date}|${p.techId}`);
        },
        onSuccess: (keys) => {
            setAppliedDays((prev) => {
                const next = new Set(prev);
                for (const k of keys) next.add(k);
                return next;
            });
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["dispatch-tech-routes"] });
        },
    });

    const proposals = response?.proposals ?? [];
    const remaining = useMemo(
        () => proposals.filter((p) => !appliedDays.has(`${p.date}|${p.techId}`)),
        [proposals, appliedDays],
    );

    const toggleDay = (key: string) => {
        setExpandedDays((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    return (
        <Dialog
            open={open}
            onClose={() => {
                setAppliedDays(new Set());
                setExpandedDays(new Set());
                onClose();
            }}
            title="Auto-schedule proposals"
            className="!max-w-3xl"
        >
            <div className="space-y-3">
                <p className="text-xs text-foreground/80">
                    The system looked at every unscheduled job in the next{" "}
                    {response?.windowDays ?? 14} days, filtered to techs with the right skills,
                    and built out-and-back routes from the shop only when a cluster of jobs is
                    dense enough to fill a meaningful chunk of a day. Hard appointments and
                    customer-promised dates are honored. Two-day notice rule respected.
                </p>

                {isLoading ? (
                    <div className="flex h-32 items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-foreground/80" />
                    </div>
                ) : !response ? (
                    <p className="py-6 text-center text-sm text-foreground/80">No data loaded yet.</p>
                ) : proposals.length === 0 ? (
                    <div className="space-y-3">
                        <div className="rounded-md border border-amber-300 bg-amber-50/70 p-4 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100">
                            <div className="flex items-start gap-2">
                                <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
                                <div>
                                    <p className="font-medium">
                                        No clusters are ready to fill a day yet.
                                    </p>
                                    <p className="mt-1 text-xs">
                                        The auto-scheduler waits until enough jobs accumulate in one
                                        area before building a tech's day. Keep adding work; the
                                        proposals will surface here automatically once a cluster
                                        reaches the density threshold (currently{" "}
                                        {Math.round(response.clusterFillThreshold * 100)}% of a tech-day).
                                    </p>
                                </div>
                            </div>
                        </div>
                        {response.skipped.length > 0 && (
                            <details className="rounded-md border bg-card p-3 text-xs">
                                <summary className="cursor-pointer font-medium">
                                    Why each job is queued ({response.skipped.length})
                                </summary>
                                <ul className="mt-2 space-y-1.5">
                                    {response.skipped.slice(0, 30).map((s) => (
                                        <li key={s.jobId} className="flex items-start gap-2">
                                            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                                            <span className="font-medium">{s.jobTitle}</span>
                                            <span className="text-foreground/80">— {s.reason}</span>
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Top-line totals */}
                        <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="rounded-md border bg-muted/30 p-2 text-center">
                                <div className="text-lg font-semibold">{response.totals.jobsProposed}</div>
                                <div className="text-foreground/80">Jobs scheduled</div>
                            </div>
                            <div className="rounded-md border bg-muted/30 p-2 text-center">
                                <div className="text-lg font-semibold">{response.totals.daysWithProposals}</div>
                                <div className="text-foreground/80">Tech-days filled</div>
                            </div>
                            <div className="rounded-md border bg-muted/30 p-2 text-center">
                                <div className="text-lg font-semibold">{response.totals.jobsSkipped}</div>
                                <div className="text-foreground/80">Queued for later</div>
                            </div>
                        </div>

                        <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
                            {proposals.map((p) => {
                                const key = `${p.date}|${p.techId}`;
                                const applied = appliedDays.has(key);
                                const expanded = expandedDays.has(key);
                                const fillPct = Math.round(p.fillRatio * 100);
                                const isPairedDay = p.stops.some((s) => s.helperUserIds.length > 0);
                                const mix = p.stops.reduce(
                                    (acc, s) => {
                                        acc[s.helperValue] = (acc[s.helperValue] ?? 0) + 1;
                                        return acc;
                                    },
                                    { high: 0, medium: 0, low: 0 } as Record<HelperValueTier, number>,
                                );
                                return (
                                    <div
                                        key={key}
                                        className={`rounded-md border bg-card text-sm ${applied ? "opacity-60" : ""}`}
                                    >
                                        <button
                                            type="button"
                                            className="flex w-full items-center justify-between gap-3 p-3 text-left"
                                            onClick={() => toggleDay(key)}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-3.5 w-3.5 text-foreground/80" />
                                                    <span className="font-medium">{fmtDate(p.date)}</span>
                                                    <span className="text-foreground/80">·</span>
                                                    <span className="font-medium">{p.techName}</span>
                                                    <Badge variant="outline" className="text-[10px]">
                                                        <MapPin className="mr-1 h-2.5 w-2.5" />
                                                        {p.clusterLabel}
                                                    </Badge>
                                                </div>
                                                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-foreground/80">
                                                    <span className={`rounded-full px-2 py-0.5 ${fillTone(p.fillRatio)}`}>
                                                        {fillPct}% of day · {p.totalBookedMinutes} min
                                                    </span>
                                                    {p.productivePoints != null && p.pointsTarget != null && (
                                                        <span
                                                            className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary"
                                                            title={`Productive points: aiming for ${p.pointsTarget}/day (cap ${p.pointsCap ?? "—"}). Travel pay is separate.`}
                                                        >
                                                            {p.productivePoints} / {p.pointsTarget} pts
                                                            {p.weeklyPoints != null && p.weeklyTarget != null && (
                                                                <> · wk {p.weeklyPoints}/{p.weeklyTarget}</>
                                                            )}
                                                        </span>
                                                    )}
                                                    <span>{p.stops.length} stops</span>
                                                    {isPairedDay && (
                                                        <span
                                                            className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700 dark:border-violet-800 dark:bg-violet-900/40 dark:text-violet-200"
                                                            title="Helper-value mix on this paired-lead day. The auto-scheduler tries to land profitable work (high/medium) here first."
                                                        >
                                                            Paired day: {mix.high} high · {mix.medium} medium · {mix.low} low
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant={applied ? "outline" : "default"}
                                                disabled={applied || applyDayMutation.isPending}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    applyDayMutation.mutate(p);
                                                }}
                                            >
                                                {applied ? "Scheduled" : "Apply day"}
                                            </Button>
                                            <ChevronRight
                                                className={`h-4 w-4 text-foreground/80 transition-transform ${expanded ? "rotate-90" : ""}`}
                                            />
                                        </button>

                                        {expanded && (
                                            <ol className="space-y-1 border-t bg-muted/10 p-3 text-xs">
                                                {p.stops.map((s, idx) => (
                                                    <li
                                                        key={s.jobId}
                                                        className="flex items-start gap-3 rounded-sm bg-card p-2"
                                                    >
                                                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-100 text-[10px] font-semibold text-violet-700 dark:bg-violet-500/30 dark:text-violet-100">
                                                            {idx + 1}
                                                        </span>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-start gap-2">
                                                                <div className="min-w-0 flex-1 font-medium">{s.jobTitle}</div>
                                                                {(() => {
                                                                    const hv = HELPER_VALUE_BADGE[s.helperValue];
                                                                    return (
                                                                        <span
                                                                            title={hv.tip}
                                                                            className={`shrink-0 rounded-full border px-1.5 py-0 text-[10px] font-semibold leading-4 ${hv.cls} ${s.helperPrioritized ? "ring-1 ring-emerald-400/50" : ""}`}
                                                                        >
                                                                            {hv.label}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </div>
                                                            <div className="mt-0.5 text-[11px] text-foreground/80">
                                                                {fmtTime(s.arriveAt)} – {fmtTime(s.departAt)}
                                                                {s.distanceFromPrevMiles > 0 &&
                                                                    ` · ${s.distanceFromPrevMiles.toFixed(1)} mi from prev`}
                                                                {helperLabel(s.helperUserIds)}
                                                            </div>
                                                            <div className="mt-1 italic text-foreground/80">
                                                                {s.reason}
                                                            </div>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ol>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {response.skipped.length > 0 && (
                            <details className="rounded-md border bg-card p-3 text-xs">
                                <summary className="cursor-pointer font-medium">
                                    Queued for later ({response.skipped.length})
                                </summary>
                                <ul className="mt-2 space-y-1.5">
                                    {response.skipped.slice(0, 30).map((s) => (
                                        <li key={s.jobId} className="flex items-start gap-2">
                                            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                                            <span className="font-medium">{s.jobTitle}</span>
                                            <span className="text-foreground/80">— {s.reason}</span>
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}

                        <div className="flex items-center justify-between border-t pt-3">
                            <div className="text-xs text-foreground/80">
                                {appliedDays.size} of {proposals.length} day-plans applied
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={onClose}>
                                    Close
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => applyAllMutation.mutate(remaining)}
                                    disabled={remaining.length === 0 || applyAllMutation.isPending}
                                >
                                    {applyAllMutation.isPending && (
                                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                    )}
                                    <Wand2 className="mr-1 h-3.5 w-3.5" />
                                    Apply all remaining ({remaining.length})
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Dialog>
    );
}

export type { AutoScheduleResponse, AutoScheduleDayProposal };
